import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '../../components/Button/Button';
import CarbonFootprintSummary from '../../components/CarbonFootprintSummary/CarbonFootprintSummary';
import CarbonOffsetCard from '../../components/DetailsCard/components/CarbonOffsetCard/CarbonOffsetCard';
import PageLayout from '../../components/Layout/PageLayout/PageLayout';
import { MainTitleStyled } from '../../components/Layout/PageLayout/PageLayout.styled';
import Popup from '../../components/Popup/Popup';
import SidebarDetails from '../../components/SidebarDetails/SidebarDetails';
import { useLocale } from '../../context/LocaleContext';
import {
  ControlsWrapperStyled,
  NoteStyled,
  PopupTitleStyled,
  SubTitleStyled,
} from './CarbonOffsetPage.styled';
import useCarbonOffsets from './hooks/useCarbonOffsets';

const CarbonOffsetsPage = () => {
  const { selectedLocale } = useLocale();
  const {
    isLoading,
    carbonOffsetProjects,
    selectedCarbonOffsetProjects,
    equivalents,
    offsets,
    hasSegments,
    onSelectCarbonOffset,
    goToPayment,
    goToSustainableActions,
    onClickBack,
  } = useCarbonOffsets();
  const [isPopupVisible, setPopupVisible] = useState(false);

  const projectsQty = carbonOffsetProjects.length;

  return (
    <>
      <PageLayout
        onClickBack={onClickBack}
        isLoading={isLoading}
        backButtonLabel={
          hasSegments ? (
            <FormattedMessage
              id="button.backToTransportation"
              defaultMessage="Back to Your Transportation"
            />
          ) : (
            <FormattedMessage id="button.backToHome" defaultMessage="Back to Home" />
          )
        }
        topSectionContent={
          <>
            <MainTitleStyled level={1}>
              <FormattedMessage
                id="breadcrumbs.offsetYourCarbonFootprint"
                defaultMessage="Offset your carbon footprint"
              />
            </MainTitleStyled>

            <p>
              <FormattedMessage
                id="offset.description"
                defaultMessage="Offsetting is an internationally adopted way to lower greenhouse gas emissions
                      that threaten our environment. Select from the list of verified Carbon Offset
                      Projects to neutralize the impact of your stay and or trip."
              />
            </p>

            <p>
              <strong>
                {projectsQty}{' '}
                <FormattedMessage id="footprint.projectsFound" defaultMessage="Projects found" />
              </strong>
            </p>
          </>
        }
        leftSideContent={carbonOffsetProjects.map(
          (
            {
              id,
              name,
              content,
              impact,
              defaultPhotoUrl,
              basePrice,
              selectedQty,
              industry,
              country,
              serialNumbersFree,
            },
            index
          ) => (
            <CarbonOffsetCard
              key={id + index}
              name={name[selectedLocale]}
              impact={impact.description[selectedLocale]}
              description={content[selectedLocale]}
              image={defaultPhotoUrl}
              price={basePrice}
              qty={selectedQty}
              type={industry.name[selectedLocale]}
              country={country.name[selectedLocale]}
              maxAvailable={serialNumbersFree}
              onChangeCounter={qty => onSelectCarbonOffset({ selectedQty: qty, selectedId: id })}
            />
          )
        )}
        rightSideContent={
          <SidebarDetails
            selectedCarbonOffsetProjects={selectedCarbonOffsetProjects}
            onDeleteCarbonOffset={id => onSelectCarbonOffset({ selectedQty: 0, selectedId: id })}
            onClickPurchase={() => setPopupVisible(true)}
          >
            {!!equivalents.length && (
              <CarbonFootprintSummary equivalents={equivalents} offsets={offsets} />
            )}
          </SidebarDetails>
        }
      />

      <Popup isOpen={isPopupVisible} onClose={() => setPopupVisible(false)}>
        <>
          <PopupTitleStyled level={2} isUppercase={false}>
            <FormattedMessage id="sustainableActions.title" defaultMessage="Sustainable Actions" />
          </PopupTitleStyled>

          <FormattedMessage
            id="home.sustainableActionsSection.text1"
            values={{
              p: (...chunks: string[]) => <p>{chunks}</p>,
              condition: false,
            }}
          />

          <NoteStyled>
            <FormattedMessage
              id="home.sustainableActionsSection.noteText"
              defaultMessage="*Alignment with SDG's does not in any way indicate a project is endorsed by the United Nations."
            />
          </NoteStyled>

          <SubTitleStyled level={3} isUppercase={false}>
            <FormattedMessage
              id="sustainableActions.question"
              defaultMessage="Do you want to add a Sustainable Actions?"
            />
          </SubTitleStyled>

          <ControlsWrapperStyled>
            <Button isInverted onClick={goToSustainableActions}>
              <FormattedMessage id="common.yes" defaultMessage="Yes" />
            </Button>

            <Button onClick={goToPayment}>
              <FormattedMessage id="common.no" defaultMessage="No" />
            </Button>
          </ControlsWrapperStyled>
        </>
      </Popup>
    </>
  );
};

export default CarbonOffsetsPage;
